'use client';

import { Label } from '@magicschool/spellbook/components/Label';
import type { SwitchGroupProps } from '@magicschool/spellbook/components/SwitchGroup';
import { Text } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn, mergeIds } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as SwitchPrimitive from 'radix-ui/switch';

export type SwitchProps = React.ComponentProps<typeof SwitchPrimitive.Root> &
  VariantProps<typeof switchVariants> & {
    /** The text label displayed next to the switch. Required for accessibility — linked via `aria-labelledby`. */
    label: string;
    /** Supplementary text displayed below the label. Linked to the switch via `aria-describedby`. */
    description?: string;
    /** When `true`, marks the switch as invalid with an error border and sets `aria-invalid="true"`. Use with form validation. */
    invalid?: boolean;
    /** The value attribute for the underlying input, useful when the switch is part of a form submission. */
    value?: string;
    /** Set automatically by `SwitchGroup` — controls width behavior based on the parent group's layout direction. Do not set manually. */
    groupOrientation?: SwitchGroupProps['orientation'];
  };

export const switchVariants = cva(
  [
    'peer',
    'group/switch',
    'inline-flex',
    'h-4',
    'w-7.5',
    'mt-px',
    'shrink-0',
    'rounded-full',
    'relative',
    'transition-colors',
    'items-center',
    'outline-none',
    'cursor-pointer',
    'bg-default-icon',
    'border-2',
    'border-transparent',
    'hover:bg-default-solid-background',
    'active:bg-default-solid-background-hover',
    'aria-checked:bg-primary-solid-background',
    'aria-checked:hover:bg-primary-solid-background-hover',
    'aria-checked:active:bg-primary-solid-background-active',
    'aria-checked:text-primary-solid-foreground-text',
    'focus-visible-ring',
    'aria-invalid:!border-error-stroke',
    'aria-invalid:hover:!border-error-stroke',
    'aria-disabled:cursor-not-allowed',
    'aria-disabled:border-transparent',
    'aria-disabled:bg-disabled-background',
    'aria-checked:aria-disabled:bg-disabled-background',
    'disabled:pointer-events-auto',
  ],
  {
    variants: {
      position: {
        start: '',
        end: '',
      },
      showLabel: {
        true: '',
      },
    },
  },
);

export const switchWrapperVariants = cva('flex items-start gap-1.5', {
  variants: {
    position: {
      start: 'flex-row justify-start',
      end: 'flex-row-reverse w-full justify-between',
    },
    groupOrientation: {
      horizontal: '',
      vertical: '',
    },
  },
  compoundVariants: [
    {
      groupOrientation: 'horizontal',
      position: 'end',
      className: 'min-w-0 w-fit',
    },
    {
      groupOrientation: 'vertical',
      position: 'end',
      className: 'sm:min-w-form-field-min',
    },
  ],
});

export const switchDescriptionVariants = cva('flex flex-col items-start justify-start', {
  variants: {
    showLabel: {
      false: 'sr-only',
    },
  },
});

/**
 * Toggle switch for immediate on/off state changes. Built on Radix UI Switch.
 *
 * ### When to use
 * - Settings that take effect immediately (e.g., enable/disable a feature)
 * - Binary toggles where the user expects instant feedback
 * - Preferences and feature flags in admin panels
 *
 * ### When NOT to use
 * - For opt-in/opt-out choices confirmed by a separate action (e.g., a save/submit button): use `Checkbox` instead
 * - For grouping multiple related toggles with shared layout: use `SwitchGroup` instead
 * - For selecting from multiple options: use `RadioGroup` or `CheckboxGroup` instead
 *
 * ### API
 * - `label`: Required. Visible text linked to the switch via `aria-labelledby`.
 * - `description`: Optional helper text below the label, linked via `aria-describedby`.
 * - `showLabel`: When `false`, visually hides the label while keeping it accessible to screen readers.
 * - `position`: Controls switch/label order: `'start'` (default) places the switch before the label, `'end'` places it after (right-aligned in LTR).
 * - `invalid`: Sets `aria-invalid="true"` and applies error border styling.
 * - `checked` / `defaultChecked`: Controlled or uncontrolled toggle state.
 * - `onCheckedChange`: Callback fired when the toggle state changes.
 *
 * ### Visual states
 * - **Off**: Gray track with thumb positioned at the start
 * - **On**: Primary-colored track with thumb positioned at the end
 * - **Disabled**: Muted track and thumb, `aria-disabled="true"`, cursor changes to `not-allowed`
 * - **Invalid**: Error-colored border via `aria-invalid`
 * - **Focused**: Visible focus ring for keyboard navigation
 *
 * ### Accessibility
 * - `label` is required and linked via `aria-labelledby`
 * - `description` is linked via `aria-describedby` when provided
 * - Disabled state uses `aria-disabled` and suppresses `onCheckedChange` callbacks
 * - Keyboard accessible: Space to toggle, Tab to navigate
 * - Supports RTL layout with mirrored thumb transition
 * - Wrapping in a `Tooltip.Trigger` is not recommended — the tooltip's `data-state` can conflict with Radix internals and the toggle interaction can feel awkward alongside tooltip hover/focus behavior. If you must, it is safe for styling (checked styles use `aria-checked`, not `data-state`), but never wrap a disabled Switch in a Tooltip — disabled elements cannot receive focus or hover, making the tooltip inaccessible
 *
 * See the [Checkbox documentation](?path=/docs/components-checkbox--docs) for a comparison table of all selection components.
 */
export const Switch: React.FC<SwitchProps> = ({
  className,
  label,
  invalid,
  description,
  showLabel = true,
  position = 'start',
  id: providedId,
  groupOrientation,
  disabled,
  onCheckedChange,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const id = useId(providedId);
  const descriptionId = `${id}-description`;
  const labelId = `${id}-label`;

  return (
    <div className={cn(switchWrapperVariants({ position, groupOrientation }), className)} data-disabled={disabled}>
      <SwitchPrimitive.Root
        data-spellbook-name="switch"
        className={switchVariants()}
        id={id}
        onCheckedChange={!disabled ? onCheckedChange : undefined}
        disabled={disabled}
        {...(invalid ? { 'aria-invalid': true } : {})}
        aria-describedby={mergeIds(description ? descriptionId : undefined, ariaDescribedBy)}
        {...(label ? { 'aria-labelledby': labelId } : {})}
        {...(disabled ? { 'aria-disabled': true } : {})}
        {...props}
      >
        <SwitchPrimitive.Thumb
          data-spellbook-name="switch-thumb"
          className={cn([
            'bg-surface-background-0',
            'group-data-[disabled=true]/switch:bg-disabled-foreground',
            'pointer-events-none',
            'absolute',
            'size-3',
            'rounded-full',
            'ring-0',
            'top-0',
            'ltr:left-0',
            'transition-[left]',
            'data-[state=checked]:left-[14px]',
            'rtl:right-0',
            'rtl:transition-[right]',
            'rtl:data-[state=checked]:right-[14px]',
          ])}
        />
      </SwitchPrimitive.Root>
      {label && (
        <div className={switchDescriptionVariants({ showLabel })}>
          <Label className={cn(disabled ? 'cursor-default' : 'cursor-pointer')} htmlFor={id} id={labelId}>
            {label}
          </Label>
          {description && (
            <Text color="default-soft" variant="body-2" element="span" id={descriptionId}>
              {description}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
